/**
 * Token manager for handling token updates and request queuing
 */

type PendingRequest = {
  id: string
  resolve: (value: any) => void
  reject: (error: any) => void
  url: string
  options: RequestInit
  retryCount: number
  timeoutHandle: ReturnType<typeof setTimeout>
}

/**
 * How long a queued request waits for the host to answer REQUEST_TOKEN before
 * rejecting. Without this, a host that never posts SET_TOKEN (or an origin
 * check rejecting it) leaves the screen on its spinner forever with no error.
 */
const QUEUED_REQUEST_TIMEOUT_MS = 20_000

class TokenManager {
  private pendingRequests: PendingRequest[] = []
  private isWaitingForToken = false
  private tokenUpdateListeners: ((token: string | null) => void)[] = []
  private requestsInProgress = new Set<string>()
  private tokenRequested = false // Flag to prevent multiple token requests

  // Add a listener for token updates
  addTokenUpdateListener(listener: (token: string | null) => void) {
    this.tokenUpdateListeners.push(listener)
    return () => {
      const index = this.tokenUpdateListeners.indexOf(listener)
      if (index > -1) {
        this.tokenUpdateListeners.splice(index, 1)
      }
    }
  }

  // Notify all listeners when token is updated
  notifyTokenUpdate(token: string | null) {
    console.log("TokenManager: Notifying token update to", this.tokenUpdateListeners.length, "listeners")
    this.tokenUpdateListeners.forEach((listener) => listener(token))

    // Reset the token requested flag since we got a new token
    this.tokenRequested = false

    // If we have a new token, retry all pending requests
    if (token && this.pendingRequests.length > 0) {
      console.log("TokenManager: Retrying", this.pendingRequests.length, "pending requests")
      this.retryPendingRequests(token)
    }
  }

  // Generate a unique request ID based on URL and options
  private generateRequestId(url: string, options: RequestInit): string {
    const method = options.method || "GET"
    const body = options.body ? JSON.stringify(options.body) : ""
    return `${method}-${url}-${body}`
  }

  // Check if we should request a new token
  shouldRequestToken(): boolean {
    if (this.tokenRequested) {
      console.log("TokenManager: Token already requested, not requesting again")
      return false
    }
    this.tokenRequested = true
    return true
  }

  // Queue a request to be retried when a new token is available
  queueRequest(url: string, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = this.generateRequestId(url, options)

      // A duplicate of an already-queued request still gets its own entry so
      // EVERY caller's promise settles — retryPendingRequests dedupes the
      // fetch by id and fans the result out to all waiters. (Overwriting the
      // existing entry's resolve/reject would leave the first caller's
      // promise pending forever.)
      if (this.pendingRequests.some((req) => req.id === requestId)) {
        console.log("TokenManager: Request already queued — sharing its retry result", url)
      } else {
        console.log("TokenManager: Queueing request for", url)
      }
      const entry: PendingRequest = {
        id: requestId,
        resolve,
        reject,
        url,
        options,
        retryCount: 0,
        timeoutHandle: setTimeout(() => {
          const index = this.pendingRequests.indexOf(entry)
          if (index > -1) {
            this.pendingRequests.splice(index, 1)
          }
          // Un-wedge the manager once the queue drains: leaving these flags
          // set would suppress every future REQUEST_TOKEN, turning one missed
          // host answer into a permanent 20s-timeout-per-call state.
          if (this.pendingRequests.length === 0) {
            this.isWaitingForToken = false
            this.tokenRequested = false
          }
          console.warn("TokenManager: Timed out waiting for a token for", url)
          reject(
            new Error(
              "Couldn't authenticate with the host application — reload the page. " +
                "(If this keeps happening, the host never answered the token request; check ALLOWED_TOKEN_ORIGINS.)"
            )
          )
        }, QUEUED_REQUEST_TIMEOUT_MS),
      }
      this.pendingRequests.push(entry)

      // Set flag that we're waiting for a token
      this.isWaitingForToken = true
    })
  }

  // Retry all pending requests with the new token
  private async retryPendingRequests(token: string) {
    if (this.pendingRequests.length === 0) {
      return
    }

    const requests = [...this.pendingRequests]
    this.pendingRequests = []
    this.isWaitingForToken = false
    requests.forEach((request) => clearTimeout(request.timeoutHandle))

    console.log("TokenManager: Retrying", requests.length, "requests with new token")

    // Group requests by ID: duplicates share one fetch, but every queued
    // caller's promise is settled with the result.
    const requestGroups = new Map<string, PendingRequest[]>()
    requests.forEach((request) => {
      const group = requestGroups.get(request.id)
      if (group) {
        group.push(request)
      } else {
        requestGroups.set(request.id, [request])
      }
    })

    for (const group of requestGroups.values()) {
      const request = group[0]
      try {
        // Update the authorization header with the new token
        const headers = new Headers(request.options.headers)
        headers.set("Authorization", `Bearer ${token}`)

        console.log(`TokenManager: Retrying request for ${request.url}`)

        const response = await fetch(request.url, {
          ...request.options,
          headers,
        })

        // A Response body is single-read — every waiter beyond the first
        // gets its own clone.
        group.forEach((waiter, index) => {
          waiter.resolve(index === 0 ? response : response.clone())
        })
      } catch (error) {
        console.error("TokenManager: Failed to retry request for", request.url, error)
        group.forEach((waiter) => waiter.reject(error))
      }
    }
  }

  // Check if we're currently waiting for a token
  isWaitingForNewToken(): boolean {
    return this.isWaitingForToken
  }

  // Check if a specific request is already queued
  isRequestQueued(url: string, options: RequestInit): boolean {
    const requestId = this.generateRequestId(url, options)
    return this.pendingRequests.some((req) => req.id === requestId)
  }

  // Clear all pending requests (e.g., on component unmount)
  clearPendingRequests() {
    console.log("TokenManager: Clearing", this.pendingRequests.length, "pending requests")
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeoutHandle)
      request.reject(new Error("Request cancelled"))
    })
    this.pendingRequests = []
    this.isWaitingForToken = false
    this.requestsInProgress.clear()
    this.tokenRequested = false
  }
}

// Export a singleton instance
export const tokenManager = new TokenManager()
